/**
 * Pure helpers for the split client form (Profile vs Settings).
 *
 * The client form is divided into two coherent groups:
 *  - Profile: who the client is (identity, descriptive).
 *  - Settings: how TouchPoint should behave for them (operational config).
 *
 * This module owns the field partition and the diff builders so the modals
 * stay thin and the regrouping logic is unit-testable without React.
 */

import { Client, ClientLink } from "@/types";

/**
 * The shape accepted by `clientService.updateClient`. Only changed keys are
 * sent; absent keys are left untouched on the backend.
 */
export interface ClientUpdatePayload {
  clientName?: string;
  instagramStartFollowers?: number;
  facebookStartFollowers?: number;
  relationshipStartedAt?: string | null;
  links?: ClientLink[];
  websiteUrl?: string | null;
  locale?: string | null;
  sector?: string | null;
  postsPerWeek?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  location?: string | null;
  businessType?: string | null;
  serviceArea?: string | null;
  monthlyAdBudgetDefault?: number | null;
  autoBoostEnabled?: boolean;
  boostStrategy?: string | null;
  autoFetchIdeasEnabled?: boolean;
  isPortfolioVisible?: boolean;
  googleAdsCustomerId?: string | null;
  googleAdsClientPays?: boolean;
  googleAdsBilledFrom?: string | null;
}

/** String-backed form state for the Profile form. */
export interface ProfileFormValues {
  clientName: string;
  locale: string;
  sector: string;
  location: string;
  latitude: string;
  longitude: string;
  relationshipStartedAt: string;
  instagramStartFollowers: string;
  facebookStartFollowers: string;
  links: ClientLink[];
  websiteUrl: string;
}

/** Mixed form state for the Settings form. */
export interface SettingsFormValues {
  postsPerWeek: string;
  businessType: string;
  serviceArea: string;
  monthlyAdBudget: string;
  boostStrategy: string;
  autoBoostEnabled: boolean;
  autoFetchIdeasEnabled: boolean;
  isPortfolioVisible: boolean;
  googleAdsCustomerId: string;
  googleAdsClientPays: boolean;
  googleAdsBilledFrom: string;
}

export type ClientFormGroup = "profile" | "settings";

/**
 * Canonical partition of client fields into the two form groups. `status` is
 * intentionally excluded: it routes through `updateClientStatus`, not the
 * field diff. The two lists must stay disjoint (asserted in tests).
 */
export const CLIENT_FORM_FIELDS = {
  profile: [
    "clientName",
    "locale",
    "sector",
    "location",
    "latitude",
    "longitude",
    "relationshipStartedAt",
    "instagramStartFollowers",
    "facebookStartFollowers",
    "links",
    "websiteUrl",
  ],
  settings: [
    "postsPerWeek",
    "businessType",
    "serviceArea",
    "monthlyAdBudgetDefault",
    "boostStrategy",
    "autoBoostEnabled",
    "autoFetchIdeasEnabled",
    "isPortfolioVisible",
    "googleAdsCustomerId",
    "googleAdsClientPays",
    "googleAdsBilledFrom",
  ],
} as const;

/**
 * Return which form group a given client field belongs to, or null if the
 * field is not part of either form (e.g. `status`, server-only fields).
 */
export function fieldGroup(field: string): ClientFormGroup | null {
  if ((CLIENT_FORM_FIELDS.profile as readonly string[]).includes(field)) {
    return "profile";
  }
  if ((CLIENT_FORM_FIELDS.settings as readonly string[]).includes(field)) {
    return "settings";
  }
  return null;
}

const parseIntOrNull = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const parseIntOrUndefined = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const parseFloatOrNull = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = parseFloat(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Build the Profile-form diff. Returns only the keys whose values differ from
 * the persisted client. `links` are assumed pre-validated by the caller.
 */
export function buildProfileUpdate(
  values: ProfileFormValues,
  client: Client
): ClientUpdatePayload {
  const update: ClientUpdatePayload = {};

  const name = values.clientName.trim();
  if (name !== client.clientName) {
    update.clientName = name;
  }

  if (values.locale !== (client.locale || "")) {
    update.locale = values.locale || null;
  }

  if (values.sector !== (client.sector || "")) {
    update.sector = values.sector || null;
  }

  if (values.location !== (client.location || "")) {
    update.location = values.location || null;
  }

  const lat = parseFloatOrNull(values.latitude);
  if (lat !== (client.latitude ?? null)) {
    update.latitude = lat;
  }

  const lng = parseFloatOrNull(values.longitude);
  if (lng !== (client.longitude ?? null)) {
    update.longitude = lng;
  }

  if (values.relationshipStartedAt !== (client.relationshipStartedAt || "")) {
    update.relationshipStartedAt = values.relationshipStartedAt || null;
  }

  const ig = parseIntOrUndefined(values.instagramStartFollowers);
  if (ig !== client.instagramStartFollowers) {
    update.instagramStartFollowers = ig;
  }

  const fb = parseIntOrUndefined(values.facebookStartFollowers);
  if (fb !== client.facebookStartFollowers) {
    update.facebookStartFollowers = fb;
  }

  const originalLinks = client.links || [];
  if (JSON.stringify(values.links) !== JSON.stringify(originalLinks)) {
    update.links = values.links;
  }

  const website = values.websiteUrl.trim();
  if (website !== (client.websiteUrl || "")) {
    update.websiteUrl = website || null;
  }

  return update;
}

/**
 * A stored Google Ads customer ID as Google writes it: "8406294360" reads
 * "840-629-4360". Anything that is not 10 digits is shown as stored.
 */
export function formatGoogleAdsCustomerId(id: string | null | undefined): string {
  if (!id) return "";
  return /^\d{10}$/.test(id) ? `${id.slice(0, 3)}-${id.slice(3, 6)}-${id.slice(6)}` : id;
}

/**
 * Build the Settings-form diff. Returns only the keys whose values differ
 * from the persisted client.
 */
export function buildSettingsUpdate(
  values: SettingsFormValues,
  client: Client
): ClientUpdatePayload {
  const update: ClientUpdatePayload = {};

  const ppw = parseIntOrNull(values.postsPerWeek);
  if (ppw !== (client.postsPerWeek ?? null)) {
    update.postsPerWeek = ppw;
  }

  if (values.businessType !== (client.businessType || "")) {
    update.businessType = values.businessType || null;
  }

  if (values.serviceArea !== (client.serviceArea || "")) {
    update.serviceArea = values.serviceArea || null;
  }

  const adBudget = parseFloatOrNull(values.monthlyAdBudget);
  if (adBudget !== (client.monthlyAdBudgetDefault ?? null)) {
    update.monthlyAdBudgetDefault = adBudget;
  }

  if (values.boostStrategy !== (client.boostStrategy || "")) {
    update.boostStrategy = values.boostStrategy || null;
  }

  if (values.autoBoostEnabled !== Boolean(client.autoBoostEnabled)) {
    update.autoBoostEnabled = values.autoBoostEnabled;
  }

  if (values.autoFetchIdeasEnabled !== Boolean(client.autoFetchIdeasEnabled)) {
    update.autoFetchIdeasEnabled = values.autoFetchIdeasEnabled;
  }

  if (values.isPortfolioVisible !== Boolean(client.isPortfolioVisible)) {
    update.isPortfolioVisible = values.isPortfolioVisible;
  }

  // Sent as typed: the server stores 10 digits ("840-629-4360" -> "8406294360")
  // and refuses anything else, so compare digits to avoid resending a match.
  const customerId = values.googleAdsCustomerId.trim();
  if (customerId.replace(/[-\s]/g, "") !== (client.googleAdsCustomerId || "")) {
    update.googleAdsCustomerId = customerId || null;
  }

  if (values.googleAdsClientPays !== Boolean(client.googleAdsClientPays)) {
    update.googleAdsClientPays = values.googleAdsClientPays;
  }

  // A date input gives YYYY-MM-DD or "", which clears it back to the client's first job month.
  const billedFrom = values.googleAdsBilledFrom.trim() || null;
  if (billedFrom !== (client.googleAdsBilledFrom || null)) {
    update.googleAdsBilledFrom = billedFrom;
  }

  return update;
}
